import {
  Agents,
  ChatBox,
  ChatBoxAgentHeader,
  ChatBoxContent,
  ChatBoxHeader,
  ChatBoxMessagesContent,
  ChatBoxParticipantContent,
  ChatBoxWelcomeHeader,
  ChatBubble,
  ConversationEvent,
  FileBlobWithId,
  isMessageEvent,
  MessageDirection,
  WebChatAvatarType,
  WebChatCustomIcon,
  WebChatParticipant,
  WebChatPosition,
  WebChatTheme,
} from '@ucc/cc-common-ui';
import { isEqual, omit } from 'lodash-es';
import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { removeAgent, setAgent } from 'state/Agents';
import {
  participantMessageReadEvent,
  participantTypingIndicatorEvent,
  retryMessage,
  sendChatClosed,
  sendMessage,
} from 'state/Conversation/actions';
import { setError } from 'state/Error';
import { ErrorType } from 'state/Error/types';
import { dispatchMessageEvent, getBlobFromPreSignedUrl } from 'state/Events/actions';
import { EventsType } from 'state/Events/types';
import { updateInitialState } from 'state/Notifications';
import { deleteSession } from 'state/Notifications/actions';
import { NotificationAgent, WebChatInitialStateContent } from 'state/Notifications/types';
import {
  submitRatingSurvey,
  submitSmartResponse,
  submitSurveyFeedback,
} from 'state/Surveys/actions';
import { WidgetConfiguration } from 'state/Widget/types';
import { useAppDispatch, useAppSelector } from 'utils/hooks';
import { dbDelete, dbGet, dbPut } from 'utils/storage';
import {
  AgentDB,
  AgentDBValue,
  ChannelDB,
  ConversationDB,
  ConversationDBValue,
  ParticipantDB,
  WidgetDB,
  WidgetDBValue,
} from 'utils/storage/schemas';

import { useNotification } from '../../hooks/notification';
import classes from './index.module.scss';

interface LayoutProps {
  theme?: Partial<WebChatTheme>;
  events?: ConversationEvent[];
  agents?: Agents;
  customAvatarIconUrl?: string;
  widgetConfiguration?: WidgetConfiguration;
  notifications?: WebChatInitialStateContent;
}

const CHAT_BUBBLE_SIZE = 94;
const CHAT_BOX_WIDTH = 360;

const Layout: FunctionComponent<LayoutProps> = ({
  theme,
  events,
  agents,
  customAvatarIconUrl,
  widgetConfiguration,
  notifications,
}) => {
  const [opened, setOpened] = useState(false);
  const [greet, setGreet] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const [participant, setParticipant] = useState<WebChatParticipant>(null);
  const [lastMessageId, setLastMessageId] = useState('');
  const [unread, setUnread] = useState(0);
  const [subscriptionActive, setSubscriptionActive] = useState(false);
  const [fileBlobsWithId, setFileBlobsWithId] = useState<FileBlobWithId[]>([]);
  const [conversationTerminated, setConversationTerminated] = useState(true);
  const { t } = useTranslation();
  const boxRef = useRef(null);
  const dispatch = useAppDispatch();
  useNotification({ participant, widgetConfiguration, subscriptionActive });

  const showParticipantForm = theme?.webChatContactInformationSettings?.enabled && !participant;
  const agentTypingIndicatorEvent = useAppSelector(
    ({ agentTypingIndicator }) => agentTypingIndicator
  );

  const {
    shareParticipantReadReceiptConfirmation,
    shareParticipantTypingIndicator,
    shareUserReadReceiptConfirmation,
    shareUserTypingIndicator,
    conversationFlow,
  } = useAppSelector(({ webChatConfiguration }) => webChatConfiguration);

  const agent = agents[Object.keys(agents)[0]];

  useEffect(() => {
    const readWidgetState = async () => {
      const widgetState = (await dbGet(WidgetDB)) as WidgetDBValue;
      setGreet(widgetState.greet);
      setOpened(widgetState.opened);
      setShowWelcome(widgetState.welcome);
    };

    readWidgetState().catch((error: Error) => {
      if (error) {
        dispatch(setError({ type: ErrorType.DATA, message: error.message }));
      }
    });
  }, []);

  useEffect(() => {
    // CCE-688: the following fix deletes incorrect local storage participant information (if it exists) that created ghost conversations.
    // TODO:This fix can be removed in a few months.
    dbDelete(ParticipantDB, {
      discriminator: '',
    });

    const readParticipantInformation = async () => {
      const participantData = await dbGet(ParticipantDB, {
        discriminator: widgetConfiguration.appKey,
      });
      setParticipant(participantData as WebChatParticipant);
    };

    readParticipantInformation().catch((error: Error) => {
      if (error) {
        dispatch(setError({ type: ErrorType.DATA, message: error.message }));
      }
    });
  }, [widgetConfiguration]);

  useEffect(() => {
    const readConversationState = async () => {
      const conversationState = (await dbGet(ConversationDB)) as ConversationDBValue;
      if (conversationState) {
        setConversationTerminated(conversationState.conversationTerminated);
      }
    };

    readConversationState().catch((error: Error) => {
      dispatch(setError({ type: ErrorType.DATA, message: error.message }));
    });
  }, []);

  useEffect(() => {
    const readAgentInformation = async () => {
      const agentData = await dbGet(AgentDB);
      if (agentData) {
        dispatch(setAgent(agentData as NotificationAgent));
      }
    };

    readAgentInformation().catch((error: Error) => {
      dispatch(setError({ type: ErrorType.DATA, message: error.message }));
    });
  }, []);

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      dispatchMessageEvent({
        type: EventsType.CC_WEBCHAT_SET_IFRAME_SIZE,
        payload: {
          width: opened || greet ? `${CHAT_BOX_WIDTH}px` : `${CHAT_BUBBLE_SIZE}px`,
          height: `${Math.max(boxRef.current?.offsetHeight, CHAT_BUBBLE_SIZE)}px`,
        },
      });
    });
    observer.observe(boxRef.current);

    return () => observer.disconnect();
  }, [boxRef.current, opened, greet]);

  useEffect(() => {
    const writeWidgetState = async () => {
      await dbPut(WidgetDB, {
        data: {
          opened,
          greet,
          welcome: showWelcome,
        },
      });
    };
    writeWidgetState().catch((error: Error) => {
      dispatch(setError({ type: ErrorType.DATA, message: error.message }));
    });
  }, [opened, greet, showWelcome]);

  useEffect(() => {
    const inboundMessages =
      events?.filter(
        event => isMessageEvent(event) && event.direction === MessageDirection.INBOUND
      ) ?? [];
    if (inboundMessages.length > 0) {
      if (opened) {
        setUnread(0);
        setLastMessageId(inboundMessages[0].id);
      } else {
        const index = inboundMessages.findIndex(event => event.id === lastMessageId);
        if (index >= 0) {
          setUnread(index);
        }
      }
    }
  }, [events, opened, lastMessageId]);

  const handleSendMessage = (message, fileBlobsWithId) => {
    if ((message ?? '').trim() !== '' || fileBlobsWithId.length) {
      const currentFormFileIds = fileBlobsWithId.map(({ id }) => id);
      dispatch(
        sendMessage({
          body: message,
          fileBlobsWithId: fileBlobsWithId,
        })
      ).then(
        result =>
          result &&
          setFileBlobsWithId(fileBlobsWithId.filter(({ id }) => !currentFormFileIds.includes(id)))
      );
    }
  };

  const handleSurveyRating = (rating: string) => {
    dispatch(submitRatingSurvey(rating));
  };

  const handleSurveyFeedbackSubmit = (feedback: string) => {
    dispatch(submitSurveyFeedback(feedback));
  };

  const handleSmartResponseSubmit = (content: string) => {
    dispatch(submitSmartResponse(content));
  };

  const handleCloseChat = async () => {
    setSubscriptionActive(false);
    setOpened(false);
    dispatch(sendChatClosed());
    setParticipant(null);
    deleteSession();
    if (agent) {
      dispatch(removeAgent(agent));
    }
    dispatch(
      updateInitialState({
        agentReadReceipts: [],
        participant: {},
        conversationEvents: {},
      } as WebChatInitialStateContent)
    );
    Promise.all([
      dbPut(ConversationDB, {
        data: true,
        key: 'conversationTerminated',
      }),
      dbDelete(WidgetDB),
      dbDelete(AgentDB),
      dbDelete(ChannelDB),
      dbDelete(ParticipantDB, { discriminator: widgetConfiguration.appKey }),
      dbDelete(ParticipantDB, { discriminator: widgetConfiguration.appKey }),
    ]).catch((error: Error) => {
      dispatch(setError({ type: ErrorType.DATA, message: error.message }));
    });
  };

  const handleRetry = message => {
    dispatch(retryMessage(message, fileBlobsWithId));
  };

  const handleChange = useCallback(
    message => {
      // start WS subscription when user start typing
      if (!subscriptionActive && message?.length > 0) {
        setSubscriptionActive(true);
      }
    },
    [subscriptionActive]
  );

  useEffect(() => {
    // start WS subscription when user opens up webchat bubble
    if (showParticipantForm === false && open) {
      setSubscriptionActive(true);
    }
  }, [open, showParticipantForm]);

  const handleSubmitParticipant = useCallback(
    formParticipant => {
      const participantChanged =
        !conversationTerminated &&
        !isEqual(omit(participant, 'participantExternalKey'), formParticipant);

      let mergedParticipant;
      if (!participantChanged) {
        mergedParticipant = Object.assign({}, participant, formParticipant);
      } else {
        mergedParticipant = Object.assign({}, formParticipant);
      }
      const writeParticipantState = async () => {
        await dbDelete(ParticipantDB, { discriminator: widgetConfiguration.appKey });
        await dbPut(ParticipantDB, {
          data: mergedParticipant,
          discriminator: widgetConfiguration.appKey,
        });
      };
      writeParticipantState()
        .then(() => {
          setParticipant(mergedParticipant);
          setSubscriptionActive(true);
        })
        .catch((error: Error) => {
          dispatch(setError({ type: ErrorType.DATA, message: error.message }));
        });
    },
    [participant, widgetConfiguration]
  );

  if (agent) {
    dbPut(AgentDB, { data: agent as AgentDBValue }).catch((error: Error) => {
      dispatch(setError({ type: ErrorType.DATA, message: error.message }));
    });
  }

  const confirmOptions = {
    label: t('Close chat?'),
    title: t('Close chat?'),
    message: t('Conversation cannot be retrieved after closing.'),
    cancelLabel: t('No, keep chatting'),
    confirmLabel: t('Yes, close chat'),
  };

  const alignLeft = theme?.pagePosition === WebChatPosition.LEFT ?? false;
  return (
    <main ref={boxRef} className={classes.root}>
      {theme &&
        (opened ? (
          <ChatBox
            alignLeft={alignLeft}
            fontSize={theme?.font?.size}
            fontFamily={theme?.font?.family?.replaceAll('+', ' ')}>
            <ChatBoxHeader
              confirmOnClose={subscriptionActive || !conversationTerminated}
              color={theme?.color}
              onClose={handleCloseChat}
              onMinimize={() => setOpened(false)}
              confirmOptions={confirmOptions}>
              {agent ? (
                <ChatBoxAgentHeader color={theme?.color} agent={agent} />
              ) : (
                <ChatBoxWelcomeHeader
                  title={theme?.header?.messageOverride ?? t('Hi! How can we help you?')}
                />
              )}
            </ChatBoxHeader>
            <ChatBoxContent>
              {showParticipantForm ? (
                <ChatBoxParticipantContent
                  color={theme?.color}
                  field={theme?.webChatContactInformationSettings}
                  currentParticipant={participant}
                  onSubmit={handleSubmitParticipant}
                />
              ) : (
                <ChatBoxMessagesContent
                  lastMessageReadAt={
                    shareUserReadReceiptConfirmation &&
                    notifications?.agentReadReceipts?.[0]?.readAt
                  }
                  {...(shareParticipantReadReceiptConfirmation && {
                    onSendReadIndicator: () => dispatch(participantMessageReadEvent()),
                  })}
                  {...(shareParticipantTypingIndicator && {
                    onTyping: isTyping => dispatch(participantTypingIndicatorEvent(isTyping)),
                  })}
                  agentTypingIndicatorEvent={agentTypingIndicatorEvent}
                  typingIndicatorEnabled={shareUserTypingIndicator}
                  agents={agents}
                  canSend={!!notifications?.participant?.key}
                  customAvatarIconUrl={customAvatarIconUrl}
                  genericAvatarIconId={
                    theme?.avatar.type === WebChatAvatarType.GENERIC
                      ? (theme?.avatar.genericIconName as WebChatCustomIcon)
                      : null
                  }
                  onChange={handleChange}
                  messages={events}
                  isWebChat
                  color={theme?.color}
                  onRetry={handleRetry}
                  showFiles
                  onRemoveFile={id => {
                    setFileBlobsWithId(
                      fileBlobsWithId.filter(currentFile => currentFile.id !== id)
                    );
                  }}
                  onAddFile={mediaPayload =>
                    setFileBlobsWithId(fileBlobs =>
                      mediaPayload.map(({ file, id }) => ({ file, id })).concat(fileBlobs)
                    )
                  }
                  onSend={handleSendMessage}
                  fileBlobsWithId={fileBlobsWithId}
                  getFileBlob={({ externalUrl }) => getBlobFromPreSignedUrl(externalUrl)}
                  onSurveyRatingClick={handleSurveyRating}
                  onSurveyFeedbackSubmit={handleSurveyFeedbackSubmit}
                  onSmartResponsesSubmit={handleSmartResponseSubmit}
                  botName={
                    conversationFlow?.displayName
                      ? conversationFlow?.displayName
                      : conversationFlow?.title
                  }
                  botAvatar={conversationFlow?.avatar}
                />
              )}
            </ChatBoxContent>
          </ChatBox>
        ) : (
          <ChatBubble
            unread={unread}
            onGreet={() => setGreet(true)}
            chatIcon={theme?.chatIcon}
            greetingDelay={theme?.autoGreeting?.delaySeconds * 1000}
            greetingText={
              showWelcome && theme?.autoGreeting.enabled && theme?.autoGreeting.messageOverride
            }
            color={theme?.color}
            alignLeft={alignLeft}
            onClick={() => {
              setOpened(true);
              setGreet(false);
              setShowWelcome(false);
            }}
          />
        ))}
    </main>
  );
};

export default Layout;
